import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

import { PlayCircle, Plus, ArrowRight, FileText, Users, Video, MessageSquare, HelpCircle, Image, Edit, Trash2, Power, PowerOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import QuizModal from "@/components/QuizModal";

export default function TrainingModules() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedDay, setSelectedDay] = useState(1);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isCodeOfConductOpen, setIsCodeOfConductOpen] = useState(false);
  const [isReferBuddyOpen, setIsReferBuddyOpen] = useState(false);
  const [isHelpdeskOpen, setIsHelpdeskOpen] = useState(false);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [currentModule, setCurrentModule] = useState<any>(null);
  const [currentQuiz, setCurrentQuiz] = useState<any>(null);
  const [moduleToDelete, setModuleToDelete] = useState<any>(null);

  // Function to get appropriate icon for each module type
  const getModuleIcon = (module: any) => {
    // Return specific icons based on module type
    switch (module.mtype) {
      case "video":
        return <Video className="text-white h-8 w-8" />;
      case "image":
        return <Image className="text-white h-8 w-8" />;
      case "form":
        return <FileText className="text-white h-8 w-8" />;
      case "helpdesk":
        return <FileText className="text-white h-8 w-8" />;
      case "external":
        return <MessageSquare className="text-white h-8 w-8" />;
      default:
        return <PlayCircle className="text-white h-8 w-8" />;
    }
  };

  const { data: modules, isLoading } = useQuery({
    queryKey: ['/training-modules'],
  });

  const { data: quizzesData } = useQuery({
    queryKey: ['/quizzes'],
  }) as { data: { quizzes: any[] } | undefined };

  const { data: user } = useQuery({
    queryKey: ['/auth/me'],
  }) as { data: any };

  // Mutation for updating module status
  const updateModuleStatusMutation = useMutation({
    mutationFn: async ({ moduleId, isActive }: { moduleId: string; isActive: boolean }) => {
      const response = await apiRequest("PUT", `/training-modules/${moduleId}/status`, { isActive });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
      toast({
        title: "Module Status Updated",
        description: "The module status has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to update module status. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleToggleModuleStatus = (moduleId: string, currentStatus: boolean | undefined) => {
    const newStatus = !currentStatus; // undefined is treated as active (true), so !undefined = false
    updateModuleStatusMutation.mutate({ moduleId, isActive: newStatus });
  };





  const handleVideoComplete = () => {
    console.log("Video completed for module:", currentModule);
    
    // Check if there's a linked quiz for this module
    const linkedQuiz = quizzesData?.quizzes?.find((quiz: any) => quiz.moduleId === currentModule.id);
    
    if (linkedQuiz) {
      console.log("Found linked quiz:", linkedQuiz);
      setCurrentQuiz(linkedQuiz);
      setIsVideoOpen(false);
      setIsQuizOpen(true);
    } else {
      // No quiz linked, just close the video
      setIsVideoOpen(false);
      setCurrentModule(null);
    }
  };

  // Mutation for deleting training modules (admin only)
  const deleteModuleMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      const response = await apiRequest("DELETE", `/training-modules/${moduleId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Module Deleted",
        description: "Training module has been successfully deleted.",
      });
      // Invalidate and refetch modules
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to delete training module",
        variant: "destructive",
      });
    },
  });

  // Handler for edit module (admin only)
  const handleEditModule = (moduleId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLocation(`/add-training-module?edit=${moduleId}`);
  };

  // Handler for delete module (admin only)
  const handleDeleteModule = (module: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setModuleToDelete(module);
  };

  // Handler for confirming deletion
  const handleConfirmDelete = () => {
    if (moduleToDelete) {
      deleteModuleMutation.mutate(moduleToDelete.id);
      setModuleToDelete(null);
    }
  };

  const handleModuleClick = (module: any) => {
    console.log("Module clicked:", module);
    
    switch (module.mtype) {
      case "video":
        setIsVideoOpen(true);
        setCurrentModule(module);
        break;
      case "image":
        setIsImageOpen(true);
        setCurrentModule(module);
        break;
      case "form":
        if (module.externalUrl) {
          window.open(module.externalUrl, '_blank');
        }
        break;
      case "helpdesk":
        setIsHelpdeskOpen(true);
        break;
      default:
        // Handle other module types
        console.log("Module type not handled:", module.mtype);
    }
  };

  // Get available days dynamically from training modules
  const availableDays: number[] = (modules as any)?.modules 
    ? (Array.from(new Set((modules as any).modules.map((module: any) => module.day))) as number[]).sort((a: number, b: number) => a - b)
    : [1, 2, 3]; // fallback to default days

  // Filter modules by selected day and sort by sequence
  const filteredModules = (modules as any)?.modules?.filter((module: any) => module.day === selectedDay) || [];

  // Admin view only - no progress tracking, but sort by sequence
  const modulesWithProgress = filteredModules.sort((a: any, b: any) => a.sequence - b.sequence);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
            <p className="text-gray-600">Loading training modules...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="text-page-title">Knowledge Hub</h1>
            <p className="text-gray-600 mt-1" data-testid="text-page-description">
              View and manage training content for employees
            </p>
          </div>

          {user?.user?.role === 'admin' && (
            <Button 
              className="bg-servify-purple hover:bg-servify-purple-dark text-white" 
              data-testid="button-add-module"
              onClick={() => setLocation('/add-training-module')}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Module
            </Button>
          )}
        </div>

        {/* Day Navigation Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            {availableDays.map((day: number) => (
              <Button
                key={day}
                variant={selectedDay === day ? "default" : "outline"}
                className={`${
                  selectedDay === day 
                    ? 'bg-servify-purple text-white hover:bg-servify-purple-dark' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } rounded-lg px-6 py-2`}
                onClick={() => setSelectedDay(day)}
                data-testid={`button-day-${day}`}
              >
                Day {day}
              </Button>
            ))}
          </div>
        </div>

        {/* Modules List */}
        <div className="space-y-4" data-testid="modules-list">
          {modulesWithProgress.map((module: any) => (
            <Card 
              key={module.id} 
              className="transition-all duration-200 hover:shadow-md cursor-pointer bg-white border border-gray-200 rounded-lg"
              onClick={() => handleModuleClick(module)}
              data-testid={`module-card-${module.id}`}
            >
              <CardContent className="p-6 relative">
                <Badge 
                  variant="outline"
                  className="absolute top-4 right-4 bg-servify-purple/10 text-servify-purple border-servify-purple/20 font-semibold"
                  data-testid={`badge-sequence-${module.id}`}
                >
                  #{module.sequence}
                </Badge>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Module Icon */}
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center bg-servify-purple">
                      {getModuleIcon(module)}
                    </div>
                    
                    {/* Module Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-xl font-semibold text-gray-900" data-testid={`text-module-title-${module.id}`}>
                          {module.title}
                        </h2>
                        {user?.user?.role === 'admin' && (
                          <Badge 
                            variant={(module.isActive ?? true) ? "default" : "secondary"}
                            className={`ml-2 ${
                              (module.isActive ?? true) 
                                ? 'bg-green-100 text-green-800 border-green-200' 
                                : 'bg-orange-100 text-orange-800 border-orange-200'
                            }`}
                            data-testid={`badge-status-${module.id}`}
                          >
                            {(module.isActive ?? true) ? 'Active' : 'Inactive'}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-gray-600 mb-2" data-testid={`text-module-description-${module.id}`}>
                        {module.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Admin Controls and Arrow Icon */}
                  <div className="flex items-center space-x-2">
                    {/* Show edit buttons for admin users - allow editing everything */}
                    {user?.user?.role === 'admin' && (
                      <>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                              className={`px-3 py-2 font-medium ${
                                (module.isActive ?? true)
                                  ? 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 hover:border-orange-300'
                                  : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300'
                              }`}
                              data-testid={`button-toggle-${module.id}`}
                            >
                              {(module.isActive ?? true) ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-1" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-1" />
                                  Activate
                                </>
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white border border-gray-200 shadow-lg max-w-md">
                            <AlertDialogHeader className="space-y-3">
                              <AlertDialogTitle className="text-xl font-bold text-gray-900">
                                {(module.isActive ?? true) ? 'Deactivate Module' : 'Activate Module'}
                              </AlertDialogTitle>
                              <AlertDialogDescription className="text-base text-gray-700 leading-relaxed">
                                Are you sure you want to {(module.isActive ?? true) ? 'deactivate' : 'activate'} the module <strong className="text-gray-900">"{module.title}"</strong>?
                                <br /><br />
                                <span className="text-gray-600">
                                  {(module.isActive ?? true) 
                                    ? 'This will hide the module from employees. You can reactivate it anytime.'
                                    : 'This will make the module visible to employees again.'
                                  }
                                </span>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleModuleStatus(module.id, module.isActive);
                                }}
                                className={
                                  (module.isActive ?? true)
                                    ? 'bg-orange-600 hover:bg-orange-700'
                                    : 'bg-green-600 hover:bg-green-700'
                                }
                              >
                                {(module.isActive ?? true) ? 'Deactivate' : 'Activate'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleEditModule(module.id, e)}
                          className="px-3 py-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300 font-medium"
                          data-testid={`button-edit-module-${module.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleDeleteModule(module, e)}
                          className="px-3 py-2 bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300 font-medium"
                          data-testid={`button-delete-module-${module.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                    <ArrowRight className="text-gray-400 h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        
                  {modulesWithProgress.length === 0 && (
          <Card className="p-8 text-center" data-testid="no-modules-message">
            <div className="max-w-md mx-auto">
              <PlayCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Knowledge Hub Modules for Day {selectedDay}</h3>
              <p className="text-gray-600">
                Create knowledge hub modules for this day to get started with the induction program.
              </p>
            </div>
          </Card>
        )}
        </div>

        {/* Video Modal */}
        {isVideoOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">{currentModule.title}</h2>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsVideoOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

                          {/* Video Content */}
            <div className="p-6 flex flex-col items-center">
              <video
                controls
                className="w-full h-auto max-w-3xl rounded-xl shadow-lg no-seek-video"
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                autoPlay
                playsInline
                tabIndex={0}
                onEnded={handleVideoComplete}
                onTimeUpdate={(e) => {
                  // Track video progress but don't trigger completion here
                  // Completion is handled by onEnded event
                }}
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  video.dataset.lastTime = "0";
                  video.focus();
                }}
                onSeeking={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  const lastTime = parseFloat(video.dataset.lastTime || "0");
                  video.currentTime = lastTime;
                }}
                onSeeked={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  const lastTime = parseFloat(video.dataset.lastTime || "0");
                  video.currentTime = lastTime;
                }}
                onTimeUpdateCapture={(e) => {
                  const video = e.currentTarget;
                  video.dataset.lastTime = video.currentTime.toString();
                }}
                onRateChange={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  if (video.playbackRate !== 1) {
                    video.playbackRate = 1;
                  }
                }}
                onKeyDown={(e) => {
                  if (['ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onLoadedData={(e) => {
                  const video = e.currentTarget;
                  video.play().catch(error => {
                    console.log("Autoplay failed:", error);
                  });
                }}
                data-testid="module-video"
              >
                <source src={currentModule.externalUrl || currentModule.mediaFile || ""} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              

            </div>
            </div>
          </div>
        )}

        {/* Image Modal */}
        {isImageOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">{currentModule.title}</h2>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsImageOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

                          {/* Image Content */}
            <div className="p-6 flex flex-col items-center">
              <img
                src={currentModule.externalUrl || currentModule.mediaFile || ""}
                alt={currentModule.title}
                className="w-auto h-auto max-w-full max-h-full object-contain rounded-xl shadow-lg"
                data-testid="module-image"
              />
            </div>
            </div>
          </div>
        )}

        {/* Code of Conduct Modal */}
        {isCodeOfConductOpen && currentModule && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-6 relative">
                <h2 className="text-2xl font-bold text-center">Code of Conduct</h2>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsCodeOfConductOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

                          {/* Video Content */}
            <div className="p-6 flex flex-col items-center">
              <video
                controls
                className="w-full h-auto max-w-3xl rounded-xl shadow-lg no-seek-video"
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                tabIndex={0}
                onEnded={handleVideoComplete}
                onTimeUpdate={(e) => {
                  const video = e.target as HTMLVideoElement;
                  if (video.currentTime >= video.duration - 0.1) {
                    console.log("Code of Conduct video reached end via timeUpdate");
                    handleVideoComplete();
                  }
                }}
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  video.dataset.lastTime = "0";
                  video.focus();
                }}
                onSeeking={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  const lastTime = parseFloat(video.dataset.lastTime || "0");
                  video.currentTime = lastTime;
                }}
                onSeeked={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  const lastTime = parseFloat(video.dataset.lastTime || "0");
                  video.currentTime = lastTime;
                }}
                onTimeUpdateCapture={(e) => {
                  const video = e.currentTarget;
                  video.dataset.lastTime = video.currentTime.toString();
                }}
                onRateChange={(e) => {
                  e.preventDefault();
                  const video = e.currentTarget;
                  if (video.playbackRate !== 1) {
                    video.playbackRate = 1;
                  }
                }}
                onKeyDown={(e) => {
                  if (['ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                data-testid="code-of-conduct-video"
              >
                <source src={currentModule.externalUrl || currentModule.mediaFile || ""} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            </div>
          </div>
        )}



        {/* Helpdesk Modal */}
        {isHelpdeskOpen && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
              {/* Modal Header */}
              <div className="bg-servify-purple text-white p-4 relative">
                <h2 className="text-2xl font-bold text-center">Helpdesk Guide</h2>
                <p className="text-servify-purple/20 text-center mt-2">Here's a quick and easy guide to our Servify Apps Helpdesk — so you know exactly whom to reach out to.</p>
                
                {/* Close Button */}
                <button
                  onClick={() => setIsHelpdeskOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Helpdesk Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="space-y-6">
                  {/* HR Support Section */}
                  <div className="bg-servify-white-grey rounded-xl p-6">
                    <h3 className="text-xl font-bold text-servify-purple mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      HR Support
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">Servify LaunchPad</span>
                        <a href="mailto:poonam.m@servify.com" className="text-servify-purple hover:underline font-medium">poonam.m@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">Oracle HCM</span>
                        <a href="mailto:radhika.j@servify.com" className="text-servify-purple hover:underline font-medium">radhika.j@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">Zoho Expense</span>
                        <a href="mailto:shrutika.c@servify.com" className="text-servify-purple hover:underline font-medium">shrutika.c@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">ADP</span>
                        <a href="mailto:apeksha.m@servify.com" className="text-servify-purple hover:underline font-medium">apeksha.m@servify.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">TAS</span>
                        <a href="mailto:abhishek.j@servify.com" className="text-servify-purple hover:underline font-medium">abhishek.j@servify.com</a>
                      </div>
                    </div>
                  </div>

                  {/* IT Support Section */}
                  <div className="bg-servify-white-grey rounded-xl p-6">
                    <h3 className="text-xl font-bold text-servify-purple mb-4 flex items-center">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      IT Support
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">Slack Channel</span>
                        <a href="https://servify.slack.com/archives/C01EPF1MJTU" target="_blank" rel="noopener noreferrer" className="text-servify-purple hover:underline font-medium">servify.slack.com</a>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-servify-white-grey">
                        <span className="font-semibold text-servify-dark-grey">IT Helpdesk</span>
                        <a href="mailto:it_helpdesk@servify.com" className="text-servify-purple hover:underline font-medium">it_helpdesk@servify.com</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Quiz Modal for Admin */}
        <QuizModal
          isOpen={isQuizOpen}
          onClose={() => {
            setIsQuizOpen(false);
            setCurrentQuiz(null);
          }}
          quiz={currentQuiz}
          isAdmin={true}
          onQuizComplete={() => {
            // Admin doesn't need completion tracking
            setIsQuizOpen(false);
            setCurrentQuiz(null);
          }}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!moduleToDelete} onOpenChange={() => setModuleToDelete(null)}>
          <AlertDialogContent className="sm:max-w-[425px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-servify-purple flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Delete Training Module
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                Are you sure you want to delete the training module <span className="font-semibold text-servify-purple">"{moduleToDelete?.title}"</span>? 
                <br />
                <br />
                This action cannot be undone and will permanently remove the module and all associated data including quizzes, progress, and attempts.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex gap-3 pt-6">
              <AlertDialogCancel 
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 dark:border-gray-600"
                data-testid="button-cancel-delete"
                onClick={() => setModuleToDelete(null)}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700 font-semibold"
                data-testid="button-confirm-delete"
              >
                Delete Module
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
